var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { dspDetectedFilterNames, } from './framework/Config';
import { isValid, } from './framework/Driver';
import { Timeouts } from './framework/Timeouts';
import { doUdpMulticastQuery } from './framework/UdpUtil';
const physicalChannelTypes = [
    'analog_gpio_in',
    'clink_aux_in',
    'clink_aux_out',
    'clink_raw_in',
    'clink_raw_out',
    'cr_line_out',
    'cr_mic_in',
    'digital_gpio_in',
    'digital_gpio_out',
    'ir_in',
    'pstn_in',
    'pstn_out',
    'sig_gen',
    'sr_line_out',
    'sr_mic_in',
    'submix',
    'voip_in',
    'voip_out',
];
function isPhysicalChannelType(value) {
    return physicalChannelTypes.some(n => n === value);
}
const complementaryPhysicalChannels = {
    analog_gpio_in: undefined,
    clink_aux_in: 'clink_aux_out',
    clink_aux_out: 'clink_aux_in',
    clink_raw_in: 'clink_raw_out',
    clink_raw_out: 'clink_raw_in',
    cr_line_out: 'cr_mic_in',
    cr_mic_in: 'cr_line_out',
    digital_gpio_in: 'digital_gpio_out',
    digital_gpio_out: 'digital_gpio_in',
    ir_in: undefined,
    pstn_in: 'pstn_out',
    pstn_out: 'pstn_in',
    sig_gen: undefined,
    sr_mic_in: 'sr_line_out',
    sr_line_out: 'sr_mic_in',
    submix: undefined,
    voip_in: 'voip_out',
    voip_out: 'voip_in',
};
const virtualChannelTypes = ['control', 'control_array', 'mono', 'stereo'];
function isVirtualChannelType(value) {
    return virtualChannelTypes.some(n => n === value);
}
const allowedMuteOrVolumeTypes = [
    'clink_aux_in',
    'clink_aux_out',
    'clink_raw_in',
    'clink_raw_out',
    'cr_line_out',
    'cr_mic_in',
    'pstn_in',
    'pstn_out',
    'sig_gen',
    'sr_mic_in',
    'sr_line_out',
    'submix',
    'voip_in',
    'voip_out',
];
const allowedMuteOrVolumeOrDigitalTypes = [
    ...allowedMuteOrVolumeTypes,
    'digital_gpio_out',
];
const allowedTypes = {
    amplifier: allowedMuteOrVolumeTypes,
    assistiveListeningMute: allowedMuteOrVolumeTypes,
    assistiveListeningMuteStatus: allowedMuteOrVolumeOrDigitalTypes,
    assistiveListeningVolume: allowedMuteOrVolumeTypes,
    audioInputMute: allowedMuteOrVolumeTypes,
    audioInputMuteStatus: allowedMuteOrVolumeOrDigitalTypes,
    audioInputVolume: allowedMuteOrVolumeTypes,
    mics: allowedMuteOrVolumeTypes,
    micsMuteStatus: allowedMuteOrVolumeOrDigitalTypes,
    overflowRoomMute: allowedMuteOrVolumeTypes,
    overflowRoomMuteStatus: allowedMuteOrVolumeOrDigitalTypes,
    overflowRoomVolume: allowedMuteOrVolumeTypes,
    preset: [],
    receiveGain: [],
    soundReinforcementMute: allowedMuteOrVolumeTypes,
    soundReinforcementMuteStatus: allowedMuteOrVolumeOrDigitalTypes,
    soundReinforcementVolume: allowedMuteOrVolumeTypes,
    telephony: ['pstn_out', 'voip_out'],
};
export const floatRegExp = '[-+]?\\d+(?:\\.\\d*)?';
export function escape(name) {
    return name.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function isPresetMuteConfig(cfg) {
    return typeof cfg !== 'string';
}
export function unescape(name) {
    return name.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}
class Preset {
    constructor(name) {
        this.name = name;
        this.isMuteConfigured = false;
        this.filters = {};
        for (const filter of dspDetectedFilterNames) {
            this.filters[filter] = filter === 'preset';
        }
        this.detectedChannel = {
            channel: this.name,
            type: 'preset',
            filters: this.filters,
        };
    }
    get isConfigured() {
        return this.isMuteConfigured;
    }
    isValid(filter) {
        return filter === 'preset';
    }
    resetConfiguration() {
        this.isMuteConfigured = false;
    }
}
class VirtualChannelBase {
    constructor(name) {
        this.name = name;
        this.isLineReady = false; // for voip_out physical channels
        this.isLineRegistered = false; // for voip_out physical channels
        this.isMuteConfigured = false;
        this.isPresetMuteStatus = false;
        this.isTelephonyConfigured = false;
        this.isVolumeConfigured = false;
        this.mutePreset = '';
        this.phoneConnect = false;
        this.unmutePreset = '';
        this.voipCallAppearanceState = 'free';
        // Caller id (voip only) comes in two lines. Assume the first line has some
        // text identifying the caller and assume the second line has some sort of dial
        // string for the call.
        this.voipCidSipRegExp = /sip:(.+)@([A-Za-z0-9_.]+)/;
        this.voipCidExtRegExp = /(\d+)/;
    }
    get isConfigured() {
        return this.isMuteConfigured || this.isTelephonyConfigured || this.isVolumeConfigured;
    }
    destroy() {
        if (this.ringTimeout !== undefined)
            this.ringTimeout.clear();
    }
    clearCallerId() {
        this.contactName1 = undefined;
        this.contactName2 = undefined;
    }
    clearRingTimeout() {
        if (this.ringTimeout !== undefined) {
            this.ringTimeout.clear();
            this.ringTimeout = undefined;
        }
    }
    computeCallerId(call) {
        const result = {};
        let match;
        if (!this.isTelephonyConfigured)
            return;
        if (this.contactName1 !== undefined) {
            result.contactName = this.contactName1;
        }
        if (call === undefined || call.direction === 'incoming') {
            if (this.contactName2 !== undefined) {
                match = this.voipCidSipRegExp.exec(this.contactName2);
                if (match) {
                    result.dialString = match[1];
                }
                else {
                    match = this.voipCidExtRegExp.exec(this.contactName2);
                    if (match)
                        result.dialString = match[1];
                }
            }
            if (result.dialString === undefined && this.contactName1 !== undefined) {
                match = this.voipCidSipRegExp.exec(this.contactName1);
                if (match) {
                    result.dialString = match[1];
                }
                else {
                    match = this.voipCidExtRegExp.exec(this.contactName1);
                    if (match)
                        result.dialString = match[1];
                }
            }
        }
        return result;
    }
    resetConfiguration() {
        this.isMuteConfigured = false;
        this.isPresetMuteStatus = false;
        this.isTelephonyConfigured = false;
        this.isVolumeConfigured = false;
        this.mutePreset = '';
        this.unmutePreset = '';
    }
}
class VirtualChannel extends VirtualChannelBase {
    constructor(name, virtualChannelType, physicalChannelType, physicalChannelIndex) {
        super(name);
        this.virtualChannelType = virtualChannelType;
        this.physicalChannelType = physicalChannelType;
        this.physicalChannelIndex = physicalChannelIndex;
        this.filters = {};
        for (const filter of dspDetectedFilterNames) {
            this.filters[filter] = allowedTypes[filter].includes(this.physicalChannelType);
        }
        this.detectedChannel = {
            channel: this.name,
            type: this.physicalChannelType,
            filters: this.filters,
        };
    }
    isValid(filter) {
        return allowedTypes[filter].includes(this.physicalChannelType);
    }
}
class VirtualChannelGroup extends VirtualChannelBase {
    constructor(name, members) {
        super(name);
        this.members = members;
        this.filters = {};
        for (const filter of dspDetectedFilterNames) {
            this.filters[filter] = filter !== 'preset' && filter !== 'telephony';
        }
        this.detectedChannel = {
            channel: this.name,
            type: 'vcgroup',
            filters: this.filters,
        };
    }
    isValid(filter) {
        return filter !== 'telephony' && filter !== 'preset';
    }
}
function isVirtualChannel(channel) {
    return 'virtualChannelType' in channel;
}
class SoundStructureChannels {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        this._detectingPresets = false;
        this._active = false;
        this.newPresets = [];
        this.presets = [];
        this.virtualChannelGroups = [];
        this.virtualChannels = [];
        // bind callbacks
        this.addPreset = this.addPreset.bind(this);
        this.finishDetectingPresets = this.finishDetectingPresets.bind(this);
        this.addVirtualChannelGroup = this.addVirtualChannelGroup.bind(this);
        this.renameVirtualChannelGroup = this.renameVirtualChannelGroup.bind(this);
        this.removeVirtualChannelGroup = this.removeVirtualChannelGroup.bind(this);
        this.addVirtualChannel = this.addVirtualChannel.bind(this);
        this.renameVirtualChannel = this.renameVirtualChannel.bind(this);
        this.removeVirtualChannel = this.removeVirtualChannel.bind(this);
    }
    get active() {
        return this._active;
    }
    get detectingPresets() {
        return this._detectingPresets;
    }
    addPreset(match) {
        this.newPresets.push(new Preset(unescape(match[1])));
    }
    addVirtualChannel(match) {
        if (!isVirtualChannelType(match[2])) {
            this.log.error(`invalid virtual channel type: ${match[2]}`);
            return;
        }
        if (!isPhysicalChannelType(match[3])) {
            this.log.error(`invalid physical channel type: ${match[3]}`);
            return;
        }
        const name = unescape(match[1]);
        const index = parseInt(match[4], 10);
        this.virtualChannels.push(new VirtualChannel(name, match[2], match[3], index));
        if (this.active)
            this.updateDetected();
    }
    addVirtualChannelGroup(match) {
        const names = match[1].split('" "');
        const groupName = names.shift();
        const virtualChannels = [];
        if (groupName === undefined) {
            this.log.error('unexpected syntax for vcgitem command');
            return;
        }
        for (const name of names) {
            const vc = this.virtualChannels.find(vc => vc.name === name);
            if (vc !== undefined) {
                virtualChannels.push(vc);
            }
            else {
                this.log.error(`unrecognized virtual channel "${name}" in group "${groupName}"`);
            }
        }
        this.virtualChannelGroups.push(new VirtualChannelGroup(unescape(groupName), virtualChannels));
        if (this.active)
            this.updateDetected();
    }
    clearConfigured() {
        for (const vc of this.virtualChannels) {
            vc.resetConfiguration();
        }
        for (const vcg of this.virtualChannelGroups) {
            vcg.resetConfiguration();
        }
        for (const p of this.presets) {
            p.resetConfiguration();
        }
    }
    detectChannels() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectVirtualChannels();
            yield this.detectVirtualChannelGroups();
            yield this.detectPresets(true);
            this._active = true;
        });
    }
    detectPresets(wait) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._detectingPresets) {
                this.log.error('already detecting presets');
                return;
            }
            this._detectingPresets = true;
            if (!this.active)
                this.presets.length = 0;
            this.newPresets.length = 0;
            if (this.presetAsyncId !== undefined) {
                this.log.error('presetAsyncId undefined');
                this.socket.unregisterAsync(this.presetAsyncId);
            }
            this.presetAsyncId = this.socket.registerAsync(/^preset "(.+)"$/, this.addPreset, 'preset');
            if (this.socket.hasCommands('presetlist')) {
                this.log.error('presetlist command already queued');
                return;
            }
            // Don't tag presetlist commands with "poll" even though this happens during polling. The
            // reason is that we don't know if the poll commands get removed by the framework, so we
            // can't set _detectingPresets to false.
            if (wait) {
                yield this.socket.send('presetlist', /^presetlist$/, { sendTimeoutMs: 20000 });
                this.finishDetectingPresets();
            }
            else {
                if (this.presetlistAsyncId !== undefined) {
                    this.log.error('presetlistAsyncId not undefined');
                    this.socket.unregisterAsync(this.presetlistAsyncId);
                }
                this.presetlistAsyncId = this.socket.registerAsync(/^presetlist$/, this.finishDetectingPresets, 'presetlist');
                yield this.socket.sendAsync('presetlist');
            }
        });
    }
    detectVirtualChannelGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            this.virtualChannelGroups.length = 0;
            const id = this.socket.registerAsync(/^vcgitem "(.+)"$/, this.addVirtualChannelGroup, 'vcgitem');
            yield this.socket.send('vcglist', /^vcglist$/, { sendTimeoutMs: 5000 });
            this.socket.unregisterAsync(id);
            this.socket.registerAsync(/^vcgdef "(.+)"$/, this.addVirtualChannelGroup, 'vcgdef');
            this.socket.registerAsync(/^vcgrename "(.+)" "(.+)"$/, this.renameVirtualChannelGroup, 'vcgrename');
            this.socket.registerAsync(/^vcgundef "(.+)"$/, this.removeVirtualChannelGroup, 'vcgundef');
        });
    }
    detectVirtualChannels() {
        return __awaiter(this, void 0, void 0, function* () {
            this.virtualChannels.length = 0;
            const id = this.socket.registerAsync(/^vcitem "(.+)" (\S+) (\S+) (\d+)/, this.addVirtualChannel, 'vcitem');
            yield this.socket.send('vclist', /^vclist$/, { sendTimeoutMs: 5000 });
            this.socket.unregisterAsync(id);
            this.socket.registerAsync(/^vcdef "(.+)" (\S+) (\S+) (\d+)/, this.addVirtualChannel, 'vcdef');
            this.socket.registerAsync(/^vcrename "(.+)" "(.+)"$/, this.renameVirtualChannel, 'vcrename');
            this.socket.registerAsync(/^vcundef "(.+)"/, this.removeVirtualChannel, 'vcundef');
        });
    }
    finishDetectingPresets() {
        if (this.presetAsyncId !== undefined) {
            this.socket.unregisterAsync(this.presetAsyncId);
            this.presetAsyncId = undefined;
        }
        else {
            this.log.error('presetAsyncId undefined');
        }
        if (this.presetlistAsyncId !== undefined) {
            this.socket.unregisterAsync(this.presetlistAsyncId);
            this.presetlistAsyncId = undefined;
        }
        const oldPresets = this.presets.map(p => p.name).sort();
        const newPresets = this.newPresets.map(p => p.name).sort();
        const changed = !this.active || !Sutl.objEquals(oldPresets, newPresets);
        if (changed) {
            this.presets.length = 0;
            this.newPresets.forEach(p => this.presets.push(p));
            this.updateDetected();
        }
        this._detectingPresets = false;
    }
    findVirtualChannel(physicalChannelType, physicalChannelIndex) {
        for (const vc of this.virtualChannels) {
            if (vc.physicalChannelType === physicalChannelType &&
                vc.physicalChannelIndex === physicalChannelIndex) {
                return vc;
            }
        }
        return undefined;
    }
    getChannel(name) {
        const chan = this.virtualChannels.find(vc => vc.name === name);
        if (chan !== undefined)
            return chan;
        return this.virtualChannelGroups.find(vcg => vcg.name === name);
    }
    getChannels(type) {
        const chans = [];
        for (const vc of this.virtualChannels) {
            if (type === 'mute' && vc.isMuteConfigured)
                chans.push(vc);
            if (type === 'telephony' && vc.isTelephonyConfigured)
                chans.push(vc);
            if (type === 'volume' && vc.isVolumeConfigured)
                chans.push(vc);
        }
        for (const vcg of this.virtualChannelGroups) {
            if (type === 'mute' && vcg.isMuteConfigured)
                chans.push(vcg);
            if (type === 'telephony' && vcg.isTelephonyConfigured)
                chans.push(vcg);
            if (type === 'volume' && vcg.isVolumeConfigured)
                chans.push(vcg);
        }
        return chans;
    }
    getComplementaryChannel(name) {
        const vc = this.getChannel(name);
        if (vc === undefined || !isVirtualChannel(vc))
            return undefined;
        const compType = complementaryPhysicalChannels[vc.physicalChannelType];
        if (compType === undefined)
            return undefined;
        const compVc = this.findVirtualChannel(compType, vc.physicalChannelIndex);
        return compVc;
    }
    getDetectedChannels() {
        const detectedChannels = [];
        for (const vc of this.virtualChannels) {
            detectedChannels.push(vc.detectedChannel);
        }
        for (const vcg of this.virtualChannelGroups) {
            detectedChannels.push(vcg.detectedChannel);
        }
        for (const p of this.presets) {
            detectedChannels.push(p.detectedChannel);
        }
        return detectedChannels;
    }
    getPreset(name) {
        return this.presets.find(p => p.name === name);
    }
    removeVirtualChannel(match) {
        const name = unescape(match[1]);
        const channel = this.getChannel(name);
        if (channel === undefined)
            return;
        Sutl.filterInPlace(this.virtualChannels, (vc) => vc.name !== name);
        channel.destroy();
        if (this.active)
            this.updateDetected();
    }
    removeVirtualChannelGroup(match) {
        const name = unescape(match[1]);
        const channel = this.getChannel(name);
        if (channel === undefined)
            return;
        Sutl.filterInPlace(this.virtualChannelGroups, (vcg) => vcg.name !== name);
        channel.destroy();
        if (this.active)
            this.updateDetected();
    }
    renameVirtualChannel(match) {
        const fromName = unescape(match[1]);
        const toName = unescape(match[2]);
        const channel = this.getChannel(fromName);
        if (channel === undefined)
            return;
        channel.name = toName;
        if (this.active)
            this.updateDetected();
    }
    renameVirtualChannelGroup(match) {
        const fromName = unescape(match[1]);
        const toName = unescape(match[2]);
        const channel = this.getChannel(fromName);
        if (channel === undefined)
            return;
        channel.name = toName;
        if (this.active)
            this.updateDetected();
    }
    updateDetected() {
        this.state.detected.update(this.getDetectedChannels(), !this.active);
        // tell framework to validate channels
        if (this.active)
            this.framework.changePhase('configuring');
    }
}
export class SoundStructure {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        this.extraAsyncHandlers = [];
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'get ye_flask',
                succeed: /^val ye_flask "You can't get ye flask!"/,
                next: /^error "authentication required for control"/,
            },
            {
                errorCode: 'authFailed',
                send: (credentials) => {
                    const password = [undefined, ''].includes(credentials.password)
                        ? '456'
                        : credentials.password;
                    return `authenticate "admin" "${password}"`;
                },
                next: /^authenticated/,
                fail: /^error "authentication failed"/,
            },
        ];
        this.isPollingTelephony = false;
        this.isValid = true;
        this.telephonyPollTime = Date.now();
        this.timeouts = new Timeouts();
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.getDetectedChannels();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send('get dev_firmware_ver 1', /^val dev_firmware_ver 1 "(.+)"/, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const vc = this.channels.getChannel(key);
                const name = escape(key);
                let param = 'mute';
                if (vc === undefined)
                    throw new Error(`unknown virtual channel "${key}"`);
                if (isVirtualChannel(vc) && vc.physicalChannelType === 'digital_gpio_out') {
                    param = 'digital_gpio_state';
                }
                const match = yield this.socket.send(`get ${param} "${name}"`, new RegExp(`^val ${param} "${Sutl.escapeRegExp(name)}" (0|1)`), tags);
                return match[1] === '1';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const vc = this.channels.getChannel(key);
                if (vc === undefined)
                    throw new Error(`invalid virtual channel: ${key}`);
                if (vc.isPresetMuteStatus) {
                    const name = escape(value ? vc.mutePreset : vc.unmutePreset);
                    const opts = { sendTimeoutMs: 10000 }; // presets can take a while to run
                    yield this.socket.send(`run "${name}"`, new RegExp(`^ran "${Sutl.escapeRegExp(name)}`), tags, opts);
                    return value;
                }
                else {
                    const name = escape(key);
                    const match = yield this.socket.send(`set mute "${name}" ${value ? '1' : '0'}`, new RegExp(`^val mute "${Sutl.escapeRegExp(name)}" (0|1)`), tags);
                    return match[1] === '1';
                }
            }),
            asyncHandlers: {
                name: 'mute',
                regExp: new RegExp('^val (?:mute|digital_gpio_state) "(.+)" (0|1)'),
                callback: (match) => {
                    var _a;
                    const name = unescape(match[1]);
                    if ((_a = this.channels.getChannel(name)) === null || _a === void 0 ? void 0 : _a.isMuteConfigured) {
                        this.state.mutes.update(name, match[2] === '1');
                    }
                },
            },
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.name = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send('get sys_name', /^val sys_name "(.+)"/, tags);
                return unescape(match[1]);
            }),
            asyncHandlers: {
                name: 'sys_name',
                regExp: new RegExp('^val sys_name "(.+)"'),
                callback: (match) => {
                    const name = unescape(match[1]);
                    this.state.name.update(name);
                },
            },
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                return yield this.setPhoneConnect(key, true);
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                const vc = this.getAvailableCallInterface(value.callType);
                if (vc === undefined) {
                    this.log.error(`no available ${value.callType} call interfaces`);
                    return undefined;
                }
                // TODO: consider using new disabled calls feature to disable the voip interface when
                // it isn't ready or registered
                if (value.callType === 'voip') {
                    if (!vc.isLineReady) {
                        this.log.warning(`can't place call, "${vc.name}" not ready`);
                        return undefined;
                    }
                    if (!vc.isLineRegistered) {
                        this.log.warning(`can't place call, "${vc.name}" not registered`);
                        return undefined;
                    }
                }
                this.socket.send(`set phone_dial "${vc.name}" "${value.dialString}"`, new RegExp(`^val phone_dial "${Sutl.escapeRegExp(vc.name)}" "${value.dialString}"`));
                return (yield this.setPhoneConnect(vc.name, true)) ? vc.name : undefined;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const name = escape(key);
                yield this.socket.send(`set phone_dial "${name}" "${value}"`, new RegExp(`^val phone_dial "${Sutl.escapeRegExp(name)}" "${value}"`));
                return value;
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                return !(yield this.setPhoneConnect(key, false));
            }),
            receiveGainRange: { max: 10, min: -10, round: 0.1 },
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                const name = escape(key);
                yield this.socket.send(`set phone_reject "${name}"`, new RegExp(`^val phone_reject "${Sutl.escapeRegExp(name)}"`));
                const vc = this.channels.getChannel(name);
                if (vc !== undefined && isVirtualChannel(vc) && vc.physicalChannelType === 'pstn_out') {
                    vc.clearRingTimeout();
                    this.state.calls.disconnect(name);
                }
                this.suspendTelephonyPolls();
                return false;
            }),
            setReceiveGain: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const vc = this.channels.getComplementaryChannel(key);
                if (vc === undefined) {
                    this.log.warning(`missing complementary virtual channel for ${key}`);
                    return value;
                }
                return this.volume.set(vc.name, value, tags);
            }),
            asyncHandlers: {
                phoneConnect: {
                    name: 'phoneConnect',
                    regExp: /^val phone_connect "(.+)" (0|1)/,
                    callback: (match) => {
                        const name = unescape(match[1]);
                        const vc = this.channels.getChannel(name);
                        if (!(vc === null || vc === void 0 ? void 0 : vc.isTelephonyConfigured))
                            return;
                        const call = this.state.calls.values[name];
                        if (call === undefined) {
                            this.log.error(`missing call interface for "${name}"`);
                            return;
                        }
                        vc.phoneConnect = match[2] === '1';
                        if (call.callType !== 'pstn')
                            return; // use different async handlers for voip
                        if (this.framework.phase === 'controlling') {
                            if (vc.phoneConnect) {
                                this.state.calls.connect(name, { canDial: true });
                            }
                            else {
                                this.state.calls.disconnect(name);
                            }
                        }
                        vc.clearRingTimeout();
                        this.suspendTelephonyPolls();
                    },
                },
                phoneDial: {
                    name: 'phoneDial',
                    regExp: /^val phone_dial "(.+)" "(.+)"$/,
                    callback: (match) => {
                        var _a;
                        const name = unescape(match[1]);
                        if (!((_a = this.channels.getChannel(name)) === null || _a === void 0 ? void 0 : _a.isTelephonyConfigured))
                            return;
                        this.state.calls.dial(name, match[2]);
                    },
                },
                phoneRing: {
                    name: 'phoneRing',
                    regExp: /^val phone_ring "(.+)" 1/,
                    callback: (match) => {
                        const vc = this.channels.getComplementaryChannel(unescape(match[1]));
                        if (vc === undefined) {
                            this.log.error(`missing virtual channel ${unescape(match[1])}`);
                            return;
                        }
                        if (!vc.isTelephonyConfigured)
                            return;
                        if (vc.physicalChannelType !== 'pstn_out') {
                            return; // voip uses call_appearance_state
                        }
                        const call = this.state.calls.values[vc.name];
                        if (call === undefined) {
                            this.log.error(`missing call interface for "${vc.name}"`);
                            return;
                        }
                        // Sometimes an extra ringing status can come in right after the phone has been
                        // taken off-hook. Ignore that case.
                        if (call.callState === 'connected')
                            return;
                        vc.clearRingTimeout();
                        vc.ringTimeout = this.timeouts.set(() => {
                            this.log.info(`ring timeout for "${vc.name}"`);
                            vc.ringTimeout = undefined;
                            this.state.calls.disconnect(vc.name);
                        }, 7000);
                        this.state.calls.setIncoming(vc.name, 'pstn', { canDial: true });
                    },
                },
                voipCallAppearanceInfo: {
                    name: 'voipCallAppearanceInfo',
                    regExp: /^val voip_call_appearance_info "(.+)" 1 (\d+) "(.*)"$/,
                    callback: (match) => {
                        const name = unescape(match[1]);
                        const vc = this.channels.getChannel(name);
                        if (!(vc === null || vc === void 0 ? void 0 : vc.isTelephonyConfigured))
                            return;
                        const lineId = match[2];
                        const info = match[3].replace(/^To:\s*|^From:\s*/, '');
                        vc[lineId === '1' ? 'contactName1' : 'contactName2'] = info;
                        const callerId = vc.computeCallerId(this.state.calls.values[name]);
                        const isConnected = this.state.calls.values[name].callState !== 'disconnected';
                        // Don't update callerId if the call is disconnected. If we do, it generates a
                        // status message to EquipmentService, which can cause a disconnected call to
                        // get added after the first poll, and that call shows up in the recent calls.
                        if (this.framework.phase === 'controlling' && isConnected) {
                            this.state.calls.updateCall(name, callerId);
                        }
                    },
                },
                voipCallAppearanceState: {
                    name: 'voipCallAppearanceState',
                    regExp: /^val voip_call_appearance_state "(.+)" (\d+) (\S+)/,
                    callback: (match) => {
                        const name = unescape(match[1]);
                        const vc = this.channels.getChannel(name);
                        if (!(vc === null || vc === void 0 ? void 0 : vc.isTelephonyConfigured))
                            return;
                        vc.voipCallAppearanceState = match[3];
                        if (this.framework.phase !== 'controlling')
                            return;
                        if (match[3] === 'connected') {
                            this.state.calls.connect(name, { canDial: true });
                        }
                        else if (match[3] === 'free') {
                            this.state.calls.disconnect(name);
                        }
                        else if (match[3] === 'offering') {
                            // Sometimes the SoundStructure send the offering state after the connected
                            // state, so we try to prevent an incorrect state transition.
                            const call = this.state.calls.values[name];
                            if (call !== undefined &&
                                (call.callState !== 'connected' || call.start < Date.now() - 2000)) {
                                this.state.calls.setIncoming(name, 'voip', { canDial: true });
                            }
                        }
                        else if (match[3] === 'proceeding') {
                            // Sometimes the SoundStructure send the proceeding state after the
                            // connected state, so we try to prevent an incorrect state transition.
                            const call = this.state.calls.values[name];
                            if (call !== undefined &&
                                (call.callState !== 'connected' || call.start < Date.now() - 2000)) {
                                this.state.calls.setOutgoing(name, 'voip', { canDial: true });
                            }
                        }
                    },
                },
                voipLineState: {
                    name: 'voipLineState',
                    regExp: /^val voip_line_state "(.+)" 1 (\S+)/,
                    callback: (match) => {
                        const name = unescape(match[1]);
                        const vc = this.channels.getChannel(name);
                        if (!(vc === null || vc === void 0 ? void 0 : vc.isTelephonyConfigured))
                            return;
                        vc.isLineRegistered = !['line_not_registered', 'none'].includes(match[2]);
                        vc.isLineReady = match[2] !== 'none';
                        if (this.framework.phase === 'controlling') {
                            if (!vc.isLineReady)
                                this.state.calls.disconnect(name);
                        }
                    },
                },
            },
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                // Just populate all calls as disconnected for now, they will get updated when we
                // start polling.
                const calls = {};
                for (const vc of this.channels.getChannels('telephony')) {
                    if (!isVirtualChannel(vc))
                        continue;
                    if (!vc.isConfigured)
                        continue;
                    const compVc = this.channels.getComplementaryChannel(vc.name);
                    let receiveGain = 0;
                    if (compVc === undefined) {
                        this.log.warning(`missing complementary virtual channel for "${vc.name}"`);
                    }
                    else {
                        const tags = [`get.receiveGain.${compVc.name}`];
                        receiveGain = yield this.volume.get(compVc.name, tags);
                    }
                    const callType = vc.physicalChannelType === 'voip_out' ? 'voip' : 'pstn';
                    calls[vc.name] = this.state.calls.addMissing({
                        callType,
                        receiveGain,
                        canDial: true,
                    });
                }
                return calls;
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                const maxCalls = {
                    pstn: 0,
                    voip: 0,
                };
                for (const vc of this.channels.getChannels('telephony')) {
                    if (!isVirtualChannel(vc))
                        continue;
                    if (!vc.isConfigured)
                        continue;
                    const callType = vc.physicalChannelType === 'voip_out' ? 'voip' : 'pstn';
                    maxCalls[callType]++;
                }
                return maxCalls;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const name = escape(key);
                const match = yield this.socket.send(`get fader "${name}"`, new RegExp(`^val fader "${Sutl.escapeRegExp(name)}" (${floatRegExp})`), tags);
                return parseFloat(match[1]);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const name = escape(key);
                const match = yield this.socket.send(`set fader "${name}" ${value.toFixed(1)}`, new RegExp(`^val fader "${Sutl.escapeRegExp(name)}" (${floatRegExp})`), tags);
                yield this.syncVolumeMute(key, value);
                return parseFloat(match[1]);
            }),
            asyncHandlers: {
                name: 'fader',
                regExp: new RegExp(`^val fader "(.+)" (${floatRegExp})`),
                callback: (match) => {
                    const name = unescape(match[1]);
                    const volume = parseFloat(match[2]);
                    const vc = this.channels.getChannel(name);
                    if (vc === undefined)
                        return;
                    if (vc.isVolumeConfigured) {
                        this.state.volumes.update(name, volume);
                        this.syncVolumeMute(name, volume);
                    }
                    else if (isVirtualChannel(vc) &&
                        (vc.physicalChannelType === 'voip_in' || vc.physicalChannelType === 'pstn_in')) {
                        const compVc = this.channels.getComplementaryChannel(name);
                        if (compVc === undefined) {
                            this.log.warning(`missing complementary virtual channel for ${name}`);
                            return;
                        }
                        if (compVc.isTelephonyConfigured) {
                            this.state.calls.updateReceiveGain(compVc.name, volume);
                        }
                    }
                },
            },
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 12, min: -12, round: 0.1 };
                return { max: 20, min: -30, round: 0.1 }; // volume
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.socket.hasCommands())
                        return;
                    yield this.socket.send('ping', /^pong/, ['poll', 'ping']);
                }),
            },
            {
                intervalMs: 7000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.telephonyPollTime > Date.now())
                        return;
                    yield this.queryTelephonyValues();
                }),
            },
            {
                intervalMs: 10000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.channels.detectingPresets)
                        yield this.channels.detectPresets(false);
                }),
            },
        ];
        this.onCallInterfaceChanged = this.onCallInterfaceChanged.bind(this);
        this.channels = new SoundStructureChannels(socket, state, log, framework);
    }
    static discoverEquipment() {
        return __awaiter(this, void 0, void 0, function* () {
            const discoveryRegExp = /<disc><soundstructure\s+name="(.+)"\s+mac="(.+)"\s+ip="(.+)"\s+master_mac="(.+)"\s+eth_auth_mode="(.+)"\s*\/><\/disc>/;
            const results = yield doUdpMulticastQuery('SoundStructure', 'Discovery', {
                broadcast: true,
                queryCommand: 'plcmivbdisc',
                queryAddress: '255.255.255.255',
                queryPort: 52774,
                udpCommandSocketOptions: { logReceivedData: true },
            }, (message, address, port) => {
                const match = discoveryRegExp.exec(message);
                if (match) {
                    const [, name, macAddress, ipAddress] = match;
                    return {
                        equipmentTypeId: '-KD9NCAlvlhHbip4c4Eg',
                        ipAddress,
                        macAddress,
                        name,
                        type: 'soundStructure',
                    };
                }
                return undefined;
            });
            let id = 0;
            return Object.fromEntries(results.map(result => ['a' + (id++).toString(), result]));
        });
    }
    // phase callbacks
    onBeforeConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.callsSubscription !== undefined) {
                this.state.calls.unsubscribe(this.callsSubscription);
                this.callsSubscription = undefined;
            }
        });
    }
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.callsSubscription = this.state.calls.subscribe(this.onCallInterfaceChanged);
            this.extraAsyncHandlers.forEach(h => this.socket.unregisterAsync(h));
            this.extraAsyncHandlers.length = 0;
            this.volumeChannel = config.amplifier;
            // We only need to detect channels the first time we connect. After that, we keep track
            // of the configured channels.
            if (!this.channels.active) {
                yield this.socket.send('set dev_led_cycle 1 3', /^val dev_led_cycle 1 3/);
                yield this.channels.detectChannels();
            }
            this.channels.clearConfigured(); // validate methods will update configured values
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isValid = isValid(this.state.validated.value);
            this.extraAsyncHandlers.push(this.socket.registerAsync(/^ran "(.+)"$/, (match) => {
                // If the preset that ran was a full preset, then it didn't generate any
                // acknowledgements. So, we query all the values we're interested in.
                this.queryAudioValues();
            }, 'presetRan'));
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.callsSubscription !== undefined) {
                this.state.calls.unsubscribe(this.callsSubscription);
                this.callsSubscription = undefined;
            }
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            return result;
        });
    }
    // other class methods
    getAvailableCallInterface(callType) {
        const telephonyChannels = this.channels.getChannels('telephony');
        for (const vc of telephonyChannels) {
            if (!isVirtualChannel(vc))
                continue;
            if (!vc.isConfigured)
                continue;
            if (callType === 'voip') {
                if (vc.physicalChannelType !== 'voip_out')
                    continue;
            }
            else if (callType === 'pstn') {
                if (vc.physicalChannelType !== 'pstn_out')
                    continue;
            }
            else {
                continue;
            }
            if (!vc.phoneConnect)
                return vc;
        }
        return undefined;
    }
    getKey(config) {
        return isPresetMuteConfig(config) ? config.muteStatus : config;
    }
    onCallInterfaceChanged(name, oldCall) {
        const newCall = this.state.calls.values[name];
        if (oldCall === undefined || newCall === undefined)
            return;
        const vc = this.channels.getChannel(name);
        if (vc === undefined)
            return;
        if (newCall.callState !== 'disconnected') {
            const callerId = vc.computeCallerId(newCall);
            this.timeouts.set(() => this.state.calls.updateCall(name, callerId));
        }
        if (newCall.callState === oldCall.callState)
            return;
        if (newCall.callState === 'disconnected') {
            vc.contactName1 = undefined;
            vc.contactName2 = undefined;
        }
    }
    queryAudioValues(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const vc of this.channels.getChannels('mute')) {
                const mute = yield this.mute.get(vc.name, tags);
                this.state.mutes.update(vc.name, mute);
            }
            for (const vc of this.channels.getChannels('volume')) {
                const volume = yield this.volume.get(vc.name, tags);
                this.state.volumes.update(vc.name, volume);
            }
        });
    }
    queryTelephonyValues() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isValid || this.isPollingTelephony)
                return;
            const asyncHandlers = this.telephony.asyncHandlers;
            let match;
            const tags = ['poll', 'telephonyPoll'];
            this.isPollingTelephony = true;
            try {
                for (const vc of this.channels.getChannels('telephony')) {
                    if (!isVirtualChannel(vc))
                        continue;
                    if (!vc.isConfigured)
                        continue;
                    const name = escape(vc.name);
                    if (vc.physicalChannelType === 'pstn_out' && vc.ringTimeout === undefined) {
                        // Don't poll phone_connect for pstn when the ring timeout is active. The
                        // phone_connect value will report 0, which will cause us to hang up the
                        // ringing call.
                        match = yield this.socket.send(`get phone_connect "${name}"`, asyncHandlers.phoneConnect.regExp, tags);
                        asyncHandlers.phoneConnect.callback(match);
                    }
                    else if (vc.physicalChannelType === 'voip_out') {
                        match = yield this.socket.send(`get voip_call_appearance_state "${name}" 1`, asyncHandlers.voipCallAppearanceState.regExp, tags);
                        asyncHandlers.voipCallAppearanceState.callback(match);
                        match = yield this.socket.send(`get voip_call_appearance_info "${name}" 1 1`, asyncHandlers.voipCallAppearanceInfo.regExp, tags);
                        asyncHandlers.voipCallAppearanceInfo.callback(match);
                        match = yield this.socket.send(`get voip_call_appearance_info "${name}" 1 2`, asyncHandlers.voipCallAppearanceInfo.regExp, tags);
                        asyncHandlers.voipCallAppearanceInfo.callback(match);
                        match = yield this.socket.send(`get voip_line_state "${name}" 1`, asyncHandlers.voipLineState.regExp, tags);
                        asyncHandlers.voipLineState.callback(match);
                    }
                }
            }
            catch (error) {
                this.isPollingTelephony = false;
                if (!('code' in error && error.code === 'commandRemoved')) {
                    throw error;
                }
            }
            this.isPollingTelephony = false;
        });
    }
    setPhoneConnect(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            const vc = this.channels.getChannel(key);
            if (vc === undefined) {
                this.log.error(`unknown channel = "${key}"`);
                return !value;
            }
            this.suspendTelephonyPolls();
            const name = escape(key);
            const match = yield this.socket.send(`set phone_connect "${name}" ${value ? '1' : '0'}`, new RegExp(`^val phone_connect "${Sutl.escapeRegExp(name)}" (0|1)`));
            vc.phoneConnect = match[1] === '1';
            vc.clearRingTimeout();
            return vc.phoneConnect;
        });
    }
    suspendTelephonyPolls(timeMs = 2000) {
        // The phone_connect value of the pstn interface sometimes doesn't get updated for a short
        // time after its state changes. To prevent a phantom call from appearing, we suspend
        // telephony polls for a bit.
        this.telephonyPollTime = Date.now() + 2000;
        this.socket.removeCommands('telephonyPoll');
    }
    syncVolumeMute(key, volume) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volumeChannel && this.volumeChannel === key) {
                const newVolumeMute = volume <= -30;
                if (this.state.mutes.values[key] !== newVolumeMute) {
                    const value = yield this.mute.set(key, newVolumeMute, ['set.volumeMute']);
                    this.state.mutes.update(key, value);
                }
            }
        });
    }
    validate(config, configInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isPresetMuteConfig(config)) {
                const mutePreset = this.channels.getPreset(config.mutePreset);
                const muteStatus = this.channels.getChannel(config.muteStatus);
                const unmutePreset = this.channels.getPreset(config.unmutePreset);
                if (!mutePreset || !muteStatus || !unmutePreset)
                    return 'missing';
                if (muteStatus.isValid(configInfo.filter)) {
                    mutePreset.isMuteConfigured = true;
                    muteStatus.isMuteConfigured = true;
                    muteStatus.isPresetMuteStatus = true;
                    muteStatus.mutePreset = mutePreset.name;
                    muteStatus.unmutePreset = unmutePreset.name;
                    unmutePreset.isMuteConfigured = true;
                    return 'ok';
                }
                return 'incompatible';
            }
            else {
                const channel = this.channels.getChannel(config);
                if (!channel)
                    return 'missing';
                if (channel.isValid(configInfo.filter)) {
                    if (configInfo.type === 'mute') {
                        channel.isMuteConfigured = true;
                    }
                    else if (configInfo.type === 'volume') {
                        channel.isVolumeConfigured = true;
                        if (configInfo.filter === 'amplifier') {
                            channel.isMuteConfigured = true; // volume mute
                        }
                    }
                    else if (configInfo.type === 'telephony') {
                        channel.isTelephonyConfigured = true;
                    }
                    return 'ok';
                }
                return 'incompatible';
            }
        });
    }
}
// static properties
SoundStructure.channelsConfigName = 'soundStructureControlChannels';
SoundStructure.driverType = 'socketDriver';
SoundStructure.key = '-KD9NCAlvlhHbip4c4Eg';
SoundStructure.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
};
SoundStructure.socketOptions = [
    Object.assign(Object.assign({}, SoundStructure.baseSocketOptions), { port: 52774 }),
    Object.assign(Object.assign({}, SoundStructure.baseSocketOptions), { port: 52775 }),
    Object.assign(Object.assign({}, SoundStructure.baseSocketOptions), { port: 4999 }),
];
